/**
 * Regra do arrastar e soltar da Base de Conhecimento (spec 2026-10-19-kb-arrastar-e-soltar):
 * o arraste só aceita os destinos que os menus de hoje já oferecem, e o backend confere
 * tudo de novo (`mover_pasta`, permissão por organização).
 */
import type { KbDocument, KbFolderNode } from '@/api/kb'
import type { AcessoDaKb } from './acesso'
import { destinosParaMover } from './arvore'

export type Arrastado = { tipo: 'documento'; doc: KbDocument } | { tipo: 'pasta'; pasta: KbFolderNode }
export type Alvo = { tipo: 'pasta'; pasta: KbFolderNode } | { tipo: 'raiz'; organizationId: number | null }
export type Veredito = { aceita: true } | { aceita: false; motivo: string }

/** A passada dos guias (`kb_guias/passada.py`) devolve o guia à pasta dele: mover não fica. */
export const MOTIVO_GUIA = 'Este guia vem do repositório do ConsoleAI: ele fica na pasta Guias de uso do ConsoleAI.'

const SO_ADMIN = 'Pasta da plataforma: só admin'

/** Vale também para o original de um envio repetido ("Mover o original para cá"). */
export function bloqueioDeMover(d: Pick<KbDocument, 'origem'>): string | null {
  return d.origem === 'guia' ? MOTIVO_GUIA : null
}

function orgDe(x: Arrastado | Alvo): number | null {
  if (x.tipo === 'documento') return x.doc.organization_id
  if (x.tipo === 'pasta') return x.pasta.organization_id
  return x.organizationId
}

export function podeArrastar(a: Arrastado, acesso: AcessoDaKb): boolean {
  if (!acesso.gere) return false
  if (orgDe(a) === null && !acesso.admin) return false
  return a.tipo === 'pasta' || bloqueioDeMover(a.doc) === null
}

export function podeSoltar(a: Arrastado, alvo: Alvo, nos: KbFolderNode[], acesso: AcessoDaKb): Veredito {
  const recusa = (motivo: string): Veredito => ({ aceita: false, motivo })
  if (!acesso.gere) return recusa('Sem permissão para mover')
  if ((orgDe(a) === null || orgDe(alvo) === null) && !acesso.admin) return recusa(SO_ADMIN)
  if (a.tipo === 'documento' && bloqueioDeMover(a.doc)) return recusa(MOTIVO_GUIA)
  if (orgDe(a) !== orgDe(alvo)) return recusa('Outra organização')
  const destino = alvo.tipo === 'pasta' ? alvo.pasta.id : null
  if (a.tipo === 'documento') {
    if (destino === null) return recusa('Escolha uma pasta')
    return destino === a.doc.folder_id ? recusa('Já está aqui') : { aceita: true }
  }
  if (destino === a.pasta.parent_id) return recusa('Já está aqui')
  if (destino === null) return { aceita: true }
  return destinosParaMover(nos, a.pasta.id).some((d) => d.id === destino) ? { aceita: true } : recusa('Dentro dela mesma')
}

export function idDeArraste(a: Arrastado | Alvo): string {
  if (a.tipo === 'documento') return `doc:${a.doc.id}`
  if (a.tipo === 'pasta') return `pasta:${a.pasta.id}`
  return `raiz:${a.organizationId ?? 'plataforma'}`
}
